// Push notifications in this browser (Settings → Ping): the service worker (sw.ts), the
// Push API subscription, and telling the server about it. iOS only allows Web Push for a site
// added to the Home Screen and opened from there (iOS 16.4+), so the page says so.

export type PushState =
  | "unsupported" // no service worker / Push API here
  | "needs-install" // iPhone/iPad Safari: add to Home Screen first
  | "denied" // the browser's permission was refused
  | "off" // could be turned on
  | "on"; // subscribed

export const isIos = () => /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
export const isStandalone = () => matchMedia("(display-mode: standalone)").matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
const supported = () => "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

/** Registers /sw.js (once); null where service workers aren't available. */
export async function registerWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!("serviceWorker" in navigator)) return null;
  try {
    return await navigator.serviceWorker.register("/sw.js", { scope: "/" });
  } catch {
    return null;
  }
}

export async function currentSubscription(): Promise<PushSubscription | null> {
  if (!supported()) return null;
  const reg = await navigator.serviceWorker.getRegistration("/");
  return (await reg?.pushManager.getSubscription()) ?? null;
}

export async function pushState(): Promise<PushState> {
  if (!supported()) return isIos() && !isStandalone() ? "needs-install" : "unsupported";
  if (Notification.permission === "denied") return "denied";
  return (await currentSubscription()) ? "on" : "off";
}

function keyBytes(b64: string): Uint8Array {
  const s = b64.replace(/-/g, "+").replace(/_/g, "/");
  const bin = atob(s + "=".repeat((4 - (s.length % 4)) % 4));
  return Uint8Array.from(bin, (c) => c.charCodeAt(0));
}

const sameKey = (a: ArrayBuffer | null | undefined, b: Uint8Array) => !!a && a.byteLength === b.length && new Uint8Array(a).every((x, i) => x === b[i]);

/** "Chrome on macOS", "Ping app on iPhone", … so the device list is readable. */
export function deviceLabel(): string {
  const ua = navigator.userAgent;
  const os = /iPhone/.test(ua) ? "iPhone" : /iPad/.test(ua) || isIos() ? "iPad" : /Android/.test(ua) ? "Android" : /Mac OS X/.test(ua) ? "macOS" : /Windows/.test(ua) ? "Windows" : /Linux/.test(ua) ? "Linux" : "this device";
  if (isStandalone()) return `${document.documentElement.dataset.app === "ping" ? "Ping" : "MonoLog"} app on ${os}`;
  const browser = /Edg\//.test(ua) ? "Edge" : /Firefox\//.test(ua) ? "Firefox" : /Chrome\//.test(ua) ? "Chrome" : /Safari\//.test(ua) ? "Safari" : "Browser";
  return `${browser} on ${os}`;
}

async function post(path: string, method: string, body?: unknown): Promise<unknown> {
  const res = await fetch(`/api/${path}`, {
    method,
    credentials: "same-origin",
    headers: body ? { "Content-Type": "application/json" } : {},
    body: body ? JSON.stringify(body) : undefined,
  });
  const out = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
  if (!res.ok) throw new Error((out as { error?: string }).error ?? `HTTP ${res.status}`);
  return out;
}

/** Asks for permission, subscribes with the server's VAPID key and saves the subscription. */
export async function enablePush(publicKey: string): Promise<void> {
  if (!supported()) throw new Error("unsupported");
  const permission = await Notification.requestPermission();
  if (permission !== "granted") throw new Error(permission === "denied" ? "denied" : "dismissed");
  const reg = (await registerWorker()) ?? (await navigator.serviceWorker.ready);
  await navigator.serviceWorker.ready;
  const key = keyBytes(publicKey);
  let sub = await reg.pushManager.getSubscription();
  // A subscription made with an older key can't receive anything now: replace it.
  if (sub && !sameKey(sub.options.applicationServerKey, key)) {
    await sub.unsubscribe().catch(() => {});
    sub = null;
  }
  sub ??= await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key as BufferSource });
  const json = sub.toJSON() as { endpoint: string; keys: { p256dh: string; auth: string } };
  await post("notify/subscriptions", "POST", { endpoint: json.endpoint, keys: json.keys, label: deviceLabel() });
}

/** Unsubscribes this browser and removes its row (`id`, when the page knows it). */
export async function disablePush(id: string | null): Promise<void> {
  const sub = await currentSubscription();
  await sub?.unsubscribe().catch(() => {});
  if (id) await post(`notify/subscriptions/${id}`, "DELETE");
}

export async function endpointHash(): Promise<string | null> {
  const sub = await currentSubscription();
  if (!sub || !crypto.subtle) return null;
  const d = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(sub.endpoint));
  return [...new Uint8Array(d)].map((b) => b.toString(16).padStart(2, "0")).join("");
}
